import React, { useState, useEffect } from 'react';
import api from '../services/api';

const Voting = ({ user, onVoteComplete, onLogout }) => {
  const [groups, setGroups] = useState([]);
  const [votes, setVotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      const response = await api.get('/groups');
      setGroups(response.data);
      // Initialize votes with 0 for each group
      const initialVotes = {};
      response.data.forEach(group => {
        initialVotes[group._id] = 0;
      });
      setVotes(initialVotes);
    } catch (err) {
      setError('Error loading groups');
    } finally {
      setLoading(false);
    }
  };

  const updateVote = (groupId, delta) => {
    // Prevent voting for own group
    const group = groups.find(g => g._id === groupId);
    if (group && group.isOwnGroup) {
      return;
    }
    
    setVotes(prev => {
      const newVotes = { ...prev };
      const current = newVotes[groupId] || 0;
      const newValue = Math.max(0, Math.min(user.credits, current + delta));
      
      // Calculate total used credits
      const totalUsed = Object.values({ ...newVotes, [groupId]: newValue })
        .reduce((sum, val) => sum + val, 0);
      
      // Only allow if total doesn't exceed available credits
      if (totalUsed <= user.credits) {
        newVotes[groupId] = newValue;
      }
      
      return newVotes;
    });
  };

  const getTotalUsed = () => {
    return Object.values(votes).reduce((sum, val) => sum + val, 0);
  };

  const getRemaining = () => {
    return user.credits - getTotalUsed();
  };

  const handleSubmit = async () => {
    const totalUsed = getTotalUsed();
    
    if (totalUsed !== user.credits) {
      setError(`You must use all ${user.credits} credits. You used ${totalUsed}`);
      return;
    }

    // Filter out own group votes
    const voteArray = Object.entries(votes)
      .filter(([groupId, credits]) => {
        const group = groups.find(g => g._id === groupId);
        return credits > 0 && (!group || !group.isOwnGroup);
      })
      .map(([groupId, credits]) => ({ groupId, credits }));

    if (voteArray.length === 0) {
      setError('You must assign at least one credit');
      return;
    }

    setError('');
    setSubmitting(true);

    try {
      await api.post('/votes', { votes: voteArray });
      onVoteComplete();
    } catch (err) {
      setError(err.response?.data?.error || 'Error submitting vote');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        color: 'white',
        fontSize: '1.5rem'
      }}>
        Loading groups...
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      padding: '40px 20px',
      color: 'white'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <div>
            <h1 style={{ fontSize: '2.5rem', marginBottom: '10px' }}>
              Welcome, {user.username}!
            </h1>
            <p style={{ fontSize: '1.2rem', opacity: 0.9 }}>
              Available credits: <strong>{getRemaining()}</strong> / {user.credits}
            </p>
          </div>
          <button
            onClick={onLogout}
            style={{
              padding: '10px 20px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: '1px solid white',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            Logout
          </button>
        </div>

        {error && (
          <div style={{
            backgroundColor: 'rgba(244, 67, 54, 0.9)',
            color: 'white',
            padding: '15px',
            borderRadius: '12px',
            marginBottom: '30px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '20px',
          marginBottom: '40px'
        }}>
          {groups.map(group => {
            const isOwnGroup = group.isOwnGroup || false;
            return (
              <div
                key={group._id}
                style={{
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  borderRadius: '20px',
                  padding: '25px',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
                  color: '#333',
                  opacity: isOwnGroup ? 0.5 : 1,
                  position: 'relative'
                }}
              >
                {isOwnGroup && (
                  <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    backgroundColor: '#FF6600',
                    color: 'white',
                    padding: '5px 10px',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}>
                    Your group
                  </div>
                )}
                <div style={{
                  textAlign: 'center',
                  marginBottom: '15px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '80px'
                }}>
                  {group.logo && group.logo.startsWith('/') ? (
                    <img 
                      src={group.logo} 
                      alt={group.name}
                      style={{
                        maxWidth: '80px',
                        maxHeight: '80px',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : (
                    <span style={{ fontSize: '4rem' }}>{group.logo || '📦'}</span>
                  )}
                </div>
                <h3 style={{
                  fontSize: '1.3rem',
                  marginBottom: '10px',
                  textAlign: 'center',
                  color: '#4169E1'
                }}>
                  {group.name}
                </h3>
                <p style={{
                  fontSize: '0.9rem',
                  color: '#666',
                  marginBottom: '20px',
                  textAlign: 'center',
                  minHeight: '40px'
                }}>
                  {group.description}
                </p>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '15px'
                }}>
                  <button
                    onClick={() => !isOwnGroup && updateVote(group._id, -1)}
                    disabled={isOwnGroup || !votes[group._id] || votes[group._id] === 0}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      border: '2px solid #FF6600',
                      backgroundColor: isOwnGroup ? '#ccc' : (votes[group._id] > 0 ? '#FF6600' : '#ccc'),
                      color: 'white',
                      fontSize: '1.5rem',
                      cursor: isOwnGroup ? 'not-allowed' : (votes[group._id] > 0 ? 'pointer' : 'not-allowed'),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    -
                  </button>
                  <span style={{
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    minWidth: '50px',
                    textAlign: 'center',
                    color: '#4169E1'
                  }}>
                    {votes[group._id] || 0}
                  </span>
                  <button
                    onClick={() => !isOwnGroup && updateVote(group._id, 1)}
                    disabled={isOwnGroup || getRemaining() === 0}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      border: '2px solid #FF6600',
                      backgroundColor: isOwnGroup ? '#ccc' : (getRemaining() > 0 ? '#FF6600' : '#ccc'),
                      color: 'white',
                      fontSize: '1.5rem',
                      cursor: isOwnGroup ? 'not-allowed' : (getRemaining() > 0 ? 'pointer' : 'not-allowed'),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    +
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{
          textAlign: 'center',
          padding: '30px',
          backgroundColor: 'rgba(255,255,255,0.95)',
          borderRadius: '20px',
          color: '#333'
        }}>
          <p style={{
            fontSize: '1.2rem',
            marginBottom: '20px'
          }}>
            Credits used: <strong>{getTotalUsed()}</strong> / {user.credits}
          </p>
          <button
            onClick={handleSubmit}
            disabled={getTotalUsed() !== user.credits || submitting}
            style={{
              padding: '15px 40px',
              fontSize: '1.2rem',
              backgroundColor: getTotalUsed() === user.credits ? '#FF6600' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: getTotalUsed() === user.credits && !submitting ? 'pointer' : 'not-allowed',
              fontWeight: '600'
            }}
          >
            {submitting ? 'Submitting...' : 'Confirm Vote'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Voting;

